/**
 * The screen contract every later UI ticket consumes.
 *
 * A route names a `screen` id and the ticket that owns it adds
 * `apps/web/src/screens/<screen>/index.tsx` exporting a default component.
 * Nothing in this file changes when a screen lands.
 */
export type ScreenId =
  | 'dashboard'
  | 'accounts'
  | 'documents'
  | 'document-form'
  | 'document-card'
  | 'journal'
  | 'trial-balance'
  | 'account-card'
  | 'account-analysis'
  | 'chess-table'
  | 'closing'
  | 'settings'
  | 'document-types'
  | 'document-chains'
  | 'print-templates'
  | 'notifications';

export interface NavEntry {
  labelKey: string;
  order: number;
}

export interface AppRoute {
  /** Pattern as written in §7; `:id` marks a parameter segment. */
  path: string;
  screen: ScreenId;
  titleKey: string;
  /** Server-side permission mirror used for navigation and screen gating. */
  permission?: string;
  nav?: NavEntry;
}

export interface MatchedRoute {
  route: AppRoute;
  params: Record<string, string>;
}

export interface ScreenProps {
  params: Record<string, string>;
}

export const APP_ROUTES: readonly AppRoute[] = [
  { path: '/', screen: 'dashboard', titleKey: 'screenDashboard', nav: { labelKey: 'navDashboard', order: 10 } },
  {
    path: '/accounts',
    screen: 'accounts',
    titleKey: 'screenAccounts',
    permission: 'core.account.read',
    nav: { labelKey: 'screenAccounts', order: 20 },
  },
  {
    path: '/documents',
    screen: 'documents',
    titleKey: 'screenDocuments',
    permission: 'core.document.read',
    nav: { labelKey: 'navDocuments', order: 30 },
  },
  { path: '/documents/new', screen: 'document-form', titleKey: 'screenDocumentForm', permission: 'core.document.create' },
  { path: '/documents/:id', screen: 'document-card', titleKey: 'screenDocumentCard', permission: 'core.document.read' },
  {
    path: '/journal',
    screen: 'journal',
    titleKey: 'screenJournal',
    permission: 'core.journal.read',
    nav: { labelKey: 'navJournal', order: 40 },
  },
  {
    path: '/reports/trial-balance',
    screen: 'trial-balance',
    titleKey: 'screenTrialBalance',
    permission: 'core.report.read',
    nav: { labelKey: 'navTrialBalance', order: 50 },
  },
  { path: '/reports/account-card', screen: 'account-card', titleKey: 'screenAccountCard', permission: 'core.report.read' },
  {
    path: '/reports/account-analysis',
    screen: 'account-analysis',
    titleKey: 'screenAccountAnalysis',
    permission: 'core.report.read',
  },
  { path: '/reports/chess-table', screen: 'chess-table', titleKey: 'screenChessTable', permission: 'core.report.read' },
  {
    path: '/closing',
    screen: 'closing',
    titleKey: 'screenClosing',
    permission: 'core.period.read',
    nav: { labelKey: 'navClosing', order: 55 },
  },
  {
    path: '/settings',
    screen: 'settings',
    titleKey: 'screenSettings',
    permission: 'plt.settings.read',
    nav: { labelKey: 'navSettings', order: 60 },
  },
  {
    path: '/settings/document-types',
    screen: 'document-types',
    titleKey: 'screenDocumentTypes',
    permission: 'plt.settings.read',
  },
  {
    path: '/settings/document-chains',
    screen: 'document-chains',
    titleKey: 'screenDocumentChains',
    permission: 'plt.settings.read',
  },
  {
    path: '/settings/print-templates',
    screen: 'print-templates',
    titleKey: 'screenPrintTemplates',
    permission: 'plt.settings.read',
  },
  {
    path: '/notifications',
    screen: 'notifications',
    titleKey: 'screenNotifications',
    nav: { labelKey: 'navNotifications', order: 70 },
  },
];

export const NAV_ROUTES: readonly AppRoute[] = [...APP_ROUTES]
  .filter((route): route is AppRoute & { nav: NavEntry } => route.nav !== undefined)
  .sort((left, right) => left.nav.order - right.nav.order);

export function normalizePath(path: string): string {
  const withoutQuery = path.split('?')[0]?.split('#')[0] ?? '/';
  const withLeading = withoutQuery.startsWith('/') ? withoutQuery : `/${withoutQuery}`;
  if (withLeading.length > 1 && withLeading.endsWith('/')) {
    return withLeading.replace(/\/+$/, '') || '/';
  }
  return withLeading;
}

function segmentsOf(path: string): string[] {
  const normalized = normalizePath(path);
  return normalized === '/' ? [] : normalized.slice(1).split('/');
}

/** Static segments win over `:param`, so `/documents/new` beats `/documents/:id`. */
export function matchRoute(pathname: string): MatchedRoute | undefined {
  const segments = segmentsOf(pathname);
  let best: (MatchedRoute & { staticCount: number }) | undefined;

  for (const route of APP_ROUTES) {
    const pattern = segmentsOf(route.path);
    if (pattern.length !== segments.length) {
      continue;
    }
    const params: Record<string, string> = {};
    let matched = true;
    for (let index = 0; index < pattern.length; index += 1) {
      const part = pattern[index] as string;
      const actual = segments[index] as string;
      if (part.startsWith(':')) {
        params[part.slice(1)] = decodeURIComponent(actual);
        continue;
      }
      if (part !== actual) {
        matched = false;
        break;
      }
    }
    if (!matched) {
      continue;
    }
    const staticCount = pattern.filter((part) => !part.startsWith(':')).length;
    if (best === undefined || staticCount > best.staticCount) {
      best = { route, params, staticCount };
    }
  }

  return best === undefined ? undefined : { route: best.route, params: best.params };
}
